import React from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  ThemeProvider,
} from '@material-ui/core';
import {
  createAlphaColor,
  adjustBrightness,
  isLightColor,
} from './themeConfig';
import themeService from '../../services/themeService';
import { useStoreState } from '@models/RootStore';

interface AuthLayoutProps {
  theme: 'light' | 'dark';
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  logo?: string;
  onLogoError?: () => void;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({
  theme,
  title,
  subtitle,
  children,
  logo,
  onLogoError,
}) => {
  // set theme file
  const themeFile = useStoreState((s) => s.theme);
  themeService.setThemeFile(themeFile);

  // Get theme configuration from service
  const themeConfig = themeService.getThemeConfig(theme);
  const muiTheme = themeConfig.muiTheme;
  const colors = themeConfig.colors;

  // Modern background pattern
  const getBackgroundStyle = () => {
    if (theme === 'dark') {
      // Create gradient based on primary color
      const primaryColor = themeConfig.primaryColor || '#4B1C46';

      // Check if primary color is bright or saturated
      const isBright = isLightColor(primaryColor);
      const isSaturated = (() => {
        const hex = primaryColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const saturation = max === 0 ? 0 : (max - min) / max;
        return saturation > 0.6; // Consider saturated if > 60%
      })();

      // Make gradient darker for bright or saturated colors, especially from top
      const darkenFactor = isBright || isSaturated ? -60 : -40;
      const darkerPrimary = adjustBrightness(primaryColor, darkenFactor);
      const darkestPrimary = adjustBrightness(primaryColor, darkenFactor - 20);

      return {
        background: ['rgba(66, 26, 64, 1)'].indexOf(primaryColor) > -1 ? `linear-gradient(rgb(58, 24, 58), rgb(22, 18, 32))` : `linear-gradient(${darkestPrimary} 0%, ${darkerPrimary} 50%, ${primaryColor} 100%)`,
        position: 'relative' as const,
        overflow: 'hidden' as const,
      };
    } else {
      return {
        background: `linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 25%, #CBD5E1 50%, #E2E8F0 75%, #F8FAFC 100%)`,
        position: 'relative' as const,
        overflow: 'hidden' as const,
      };
    }
  };

  // Random circles pattern overlay - only for light theme
  const getPatternOverlay = () => {
    if (theme === 'light') {
      return {
        position: 'absolute' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          radial-gradient(circle at 15% 25%, ${createAlphaColor(
            colors.primary,
            0.15
          )} 0%, transparent 25%),
          radial-gradient(circle at 85% 15%, ${createAlphaColor(
            colors.primary,
            0.12
          )} 0%, transparent 20%),
          radial-gradient(circle at 45% 75%, ${createAlphaColor(
            colors.primary,
            0.14
          )} 0%, transparent 30%),
          radial-gradient(circle at 75% 85%, ${createAlphaColor(
            colors.primary,
            0.1
          )} 0%, transparent 18%),
          radial-gradient(circle at 25% 65%, ${createAlphaColor(
            colors.primary,
            0.13
          )} 0%, transparent 22%),
          radial-gradient(circle at 65% 35%, ${createAlphaColor(
            colors.primary,
            0.16
          )} 0%, transparent 28%),
          radial-gradient(circle at 90% 60%, ${createAlphaColor(
            colors.primary,
            0.11
          )} 0%, transparent 15%),
          radial-gradient(circle at 10% 85%, ${createAlphaColor(
            colors.primary,
            0.12
          )} 0%, transparent 20%),
          radial-gradient(circle at 55% 15%, ${createAlphaColor(
            colors.primary,
            0.09
          )} 0%, transparent 18%),
          radial-gradient(circle at 35% 45%, ${createAlphaColor(
            colors.primary,
            0.14
          )} 0%, transparent 25%)
        `,
        backgroundSize:
          '100% 100%, 100% 100%, 100% 100%, 100% 100%, 100% 100%, 100% 100%, 100% 100%, 100% 100%, 100% 100%',
        backgroundPosition: '0 0, 0 0, 0 0, 0 0, 0 0, 0 0, 0 0, 0 0, 0 0, 0 0',
        pointerEvents: 'none' as const,
      };
    }
    return undefined;
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <Container
        maxWidth={false}
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: window.innerWidth <= 768 ? '16px' : '24px',
          ...getBackgroundStyle(),
        }}
      >
        {/* Background pattern overlay - only for light theme */}
        {theme === 'light' && <div style={getPatternOverlay()} />}

        <Paper
          elevation={theme === 'dark' ? 8 : 4}
          style={{
            maxWidth: '600px',
            width: '100%',
            padding: window.innerWidth <= 768 ? '24px 32px' : '32px 64px',
            position: 'relative',
            zIndex: 1,
            backdropFilter: 'blur(10px)',
            backgroundColor:
              theme === 'dark'
                ? 'rgba(27, 36, 44, 0.2)'
                : 'rgba(255, 255, 255, 0.8)',
            border:
              theme === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : '1px solid rgba(75, 28, 70, 0.1)',
          }}
        >
          {/* Logo */}
          {logo && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              mb={3}
            >
              <img
                src={logo}
                alt="Logo"
                style={{
                  maxWidth: '220px',
                  height: 'auto',
                }}
                onError={onLogoError}
              />
            </Box>
          )}

          {/* Title */}
          <Typography
            variant="h1"
            align="center"
            gutterBottom
            style={{
              marginBottom: '24px',
              background:
                theme === 'dark'
                  ? 'linear-gradient(135deg, #FFFFFF 0%, #E2E8F0 100%)'
                  : 'linear-gradient(135deg, #1E293B 0%, #475569 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {title}
          </Typography>

          {/* Subtitle */}
          {subtitle && (
            <Typography
              variant="h2"
              align="center"
              color="textSecondary"
              gutterBottom
              style={{
                marginBottom: '16px',
              }}
            >
              {subtitle}
            </Typography>
          )}

          {/* Content */}
          {children}
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default AuthLayout;
